/**
 * Декодер штрихкода С ПОТОКА камеры (RP-FB4; та же копия, что у сканера продажи AQSI) — перенос донора
 * (`frontend/src/modules/catalog/components/CameraScanner/barcodeDecoders.ts`, задача 366):
 * родной `BarcodeDetector`, где он есть, иначе ZXing (`@zxing/browser`, грузится лениво).
 * За интерфейсом — чтобы экран и его тесты не зависели от камеры: jsdom её не имеет,
 * тест подставляет свой декодер и «выдаёт» коды сам.
 */
export interface BarcodeStreamDecoder {
  start(video: HTMLVideoElement, onCode: (code: string) => void): void
  stop(): void
}

export type BarcodeDecoderFactory = () => BarcodeStreamDecoder

/** Товарные коды; QR — на этикетках со ссылкой, чтобы не молчать на нём. */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code']

/** Чаще четырёх раз в секунду спрашивать родной детектор незачем — кадры те же. */
const NATIVE_POLL_MS = 250

// `BarcodeDetector` ещё не в lib.dom TypeScript — описываем ровно то, что используем.
interface DetectedBarcode {
  rawValue: string
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>
}
type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike

function nativeDetector(): BarcodeDetectorConstructor | null {
  const candidate = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector
  return typeof candidate === 'function' ? candidate : null
}

class NativeDecoder implements BarcodeStreamDecoder {
  private timer: ReturnType<typeof setInterval> | null = null
  private busy = false
  private readonly Detector: BarcodeDetectorConstructor

  constructor(Detector: BarcodeDetectorConstructor) {
    this.Detector = Detector
  }

  start(video: HTMLVideoElement, onCode: (code: string) => void): void {
    const detector = new this.Detector({ formats: FORMATS })
    this.timer = setInterval(() => {
      // Кадр ещё не пришёл или прошлый detect не закончился — пропускаем тик.
      if (this.busy || video.readyState < 2) return
      this.busy = true
      detector
        .detect(video)
        .then((found) => {
          const code = found[0]?.rawValue.trim()
          if (code !== undefined && code !== '') onCode(code)
        })
        .catch(() => {
          // Отказ детектора на конкретном кадре — не ошибка сканера.
        })
        .finally(() => {
          this.busy = false
        })
    }, NATIVE_POLL_MS)
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer)
    this.timer = null
  }
}

class ZxingDecoder implements BarcodeStreamDecoder {
  private controls: { stop(): void } | null = null
  private stopped = false

  start(video: HTMLVideoElement, onCode: (code: string) => void): void {
    this.stopped = false
    void import('@zxing/browser').then(async ({ BrowserMultiFormatReader }) => {
      if (this.stopped) return
      const reader = new BrowserMultiFormatReader()
      const controls = await reader.decodeFromVideoElement(video, (result) => {
        const code = result?.getText().trim()
        if (code !== undefined && code !== '') onCode(code)
      })
      if (this.stopped) controls.stop()
      else this.controls = controls
    })
  }

  stop(): void {
    this.stopped = true
    this.controls?.stop()
    this.controls = null
  }
}

export function createBarcodeDecoder(): BarcodeStreamDecoder {
  const Detector = nativeDetector()
  return Detector !== null ? new NativeDecoder(Detector) : new ZxingDecoder()
}
